/// <reference path="../baseClasses/dataMaintenance.interface.ts" />
/// <reference path="../baseClasses/dataServiceBase.ts" />
/// <reference path="../models/meevo.model.custom.ts" />

module Meevo.Components.Common {
    import ClientMembershipIndicatorEnum = Meevo.Model.ClientMembershipIndicatorEnum;
    import ClientMembershipIndicatorStatusEnum = Meevo.Model.ClientMembershipIndicatorStatusEnum;

    export interface IClientMembershipDates {
        expiresOnDate: string,
        accruedServiceExpireDate: string,
        renewalBeginsOn: string,
        paymentDateFrom: string,
        paymentDateTill: string
    }

    export interface ICancellationDates {
        accruedServiceExpireDate: Date,
        cancellationDateFrom: Date,
        cancellationDateTill: Date,
        terminationDateFrom: Date,
        terminationDateTill: Date,
        maxTerminationDate: Date,
        immediateCancellationAccruedServiceExpireDate: Date,
        maxAccruedServiceExpirationDate: Date,
    }

    export interface IClientMembershipIndicator {
        clientId: Guid,
        clientMembershipId: Guid,
        clientMembershipIndicatorEnum: Meevo.Model.ClientMembershipIndicatorEnum,
        clientMembershipIndicatorStatusEnum: Meevo.Model.ClientMembershipIndicatorStatusEnum,
        clientMembershipSearchStatusEnum: Meevo.Model.ClientMembershipSearchStatusEnum,
        homeLocationName: string
    }

    export class ClientMembershipDataService extends Meevo.Components.BaseClasses.DataServiceBase implements Meevo.Components.BaseClasses.IDataService
    {
        constructor(protected appSettings: Meevo.Components.Common.IAppSettings,
                    protected $http: ng.IHttpService,
                    protected $q: ng.IQService,
                    protected identityService: Meevo.Components.Common.IIdentityService,
                    protected $cookies: ng.cookies.ICookiesService,
                    protected busyService: Meevo.Components.Busy.BusyService) {
            super(appSettings, $http, $q, identityService, $cookies);
        }

        private _clientMembershipIndicatorEnumIcons = {
            [ClientMembershipIndicatorEnum.GreenStar]: 'assets/images/icons/membership_first90days.svg',
            [ClientMembershipIndicatorEnum.BlueStar]: 'assets/images/icons/membership_original.svg',
            [ClientMembershipIndicatorEnum.BlueStarWithRedX]: 'assets/images/icons/membership_expired.svg',
            [ClientMembershipIndicatorEnum.BlueStarYellowI]: 'assets/images/icons/membership_pmtdidnotprocess.svg',
            [ClientMembershipIndicatorEnum.BlueStarWithPause]: 'assets/images/icons/membership_frozen.svg',
            [ClientMembershipIndicatorEnum.BlueStarWithPauseAndPlus]: 'assets/images/icons/membership_frozen_plus.svg',
            [ClientMembershipIndicatorEnum.BlueStarWithPackage]: 'assets/images/icons/',
            [ClientMembershipIndicatorEnum.HollowedBlueStar]: 'assets/images/icons/membership_noncounting.svg',
            [ClientMembershipIndicatorEnum.RedStar]: 'assets/images/icons/membership_expire30days.svg',
            [ClientMembershipIndicatorEnum.RedCircleWithMinus]: 'assets/images/icons/membership_suspended.svg',
            [ClientMembershipIndicatorEnum.RedCircleWithLine]: 'assets/images/icons/membership_futuretermdate.svg',
            [ClientMembershipIndicatorEnum.BlueStarRedTriangleI]: 'assets/images/icons/membership_ccexpiring.svg',
            [ClientMembershipIndicatorEnum.PlatinumStar]: 'assets/images/icons/membership_renewpastterm.svg',
            [ClientMembershipIndicatorEnum.SoldToday]: 'assets/images/icons/membership_firstday.svg',
        };

        private _clientMembershipIndicatorStatusEnum = {
            [ClientMembershipIndicatorStatusEnum.DueToExpire]: 'LABEL_TOOLTIP_MEMBERSHIP_DUETOEXPIRE',
            [ClientMembershipIndicatorStatusEnum.Error]: 'LABEL_ERROR',
            [ClientMembershipIndicatorStatusEnum.Expired]: 'LABEL_TOOLTIP_MEMBERSHIP_EXPIRED',
            [ClientMembershipIndicatorStatusEnum.Frozen]: 'LABEL_TOOLTIP_MEMBERSHIP_FROZEN',
            [ClientMembershipIndicatorStatusEnum.InCancellation]: 'LABEL_TOOLTIP_MEMBERSHIP_INCANCELLATION',
            [ClientMembershipIndicatorStatusEnum.Initial90]: 'LABEL_TOOLTIP_MEMBERSHIP_INITIAL90',
            [ClientMembershipIndicatorStatusEnum.InRenewal]: 'LABEL_TOOLTIP_MEMBERSHIP_INRENEWAL',
            [ClientMembershipIndicatorStatusEnum.NonCounting]: 'LABEL_TOOLTIP_MEMBERSHIP_NONCOUNTING',
            [ClientMembershipIndicatorStatusEnum.SoldToday]: 'LABEL_TOOLTIP_MEMBERSHIP_SOLDTODAY',
            [ClientMembershipIndicatorStatusEnum.Suspended]: 'LABEL_TOOLTIP_MEMBERSHIP_SUSPENDED',
            [ClientMembershipIndicatorStatusEnum.WithinInitialTerm]: 'LABEL_TOOLTIP_MEMBERSHIP_WITHININITIALTERM',
            [ClientMembershipIndicatorStatusEnum.CCDueToExpire]: 'LABEL_TOOLTIP_MEMBERSHIP_CCDUETOEXPIRE'
        };

        /**
         * Retrieve from server all dates related to the current client membership, which are dependent on enrollment date
         * @param {Guid} id - Membership id
         * @param {Date} date - Enrollment date
         * @param {boolean} showLoading - show loading spinner. Default to true.
         * @param {Date} firstPayment - If billing type is recurring, send this param to calculate expiration date
         * @param {number} dayOfMonth - If billing type is recurring and firstRecurringPaymentDate is missed, send this param to calculate expiration date. Default to null
         * @param {Guid} clientId - Client id
         * @param {boolean} autoRenewEnabled - flag for auto renew
         * @param {string} membershipNumber - Can sell the same active membership number to two clients
         * @param {Guid} clientMembershipId - Client Membership Id that is currently being edited
         * @param {Array} clientMembershipIds - Array of Client Membership Ids that belong to a currently editing unfinalized Sale
         * @returns {Object} List of calculated dates
         * */
        getDateDependencies(id: Guid, date: Date | string, showLoading: boolean = true, firstPayment: Date | string = null, dayOfMonth: number = null, clientId: string = null,
            autoRenewEnabled: boolean = false, membershipNumber: string = '', clientMembershipId: string = null, clientMembershipIds: Array<string> = [], autoRenewType: Model.AutoRenewTypeEnum = null, isPreviewForUpgrade = false): ng.IPromise<IClientMembershipDates> {
            if (showLoading) {
                this.busyService.start();
            }
            return this.$http.post<IClientMembershipDates>(`/${this.appSettings.API_ROOT}/Membership/Client/Preview`, {
                membershipId: id,
                enrollmentDate: date,
                firstRecurringPaymentDate: firstPayment,
                paymentDayOfMonth: dayOfMonth,
                clientId: clientId,
                autoRenewEnabled: autoRenewEnabled,
                membershipNumber: membershipNumber,
                clientMembershipId: clientMembershipId,
                clientMembershipIds: clientMembershipIds,
                autoRenewType: autoRenewType,
                isPreviewForUpgrade: isPreviewForUpgrade,
            })
                .then((response) => response.data)
                .finally(() => {
                    this.busyService.stop();
                });
        }

        /**
         * Retrieve from server all dates for Cancellation (cancellationDate, terminationDate, accruedServiceExpiration)
         * @param {Guid} id - Membership id
         * @param {Object} dates - List of dates
         * @param {boolean} showLoading - show loading spinner. Default to true.
         * @returns {Object} List of calculated dates
         * */
        getDatesForCancellation(id: Guid, dates: ICancellationDates | any = {}, showLoading: boolean = true): ng.IPromise<ICancellationDates> {
            let uri = `/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.membershipManager}/${id}/Cancellation/Preview`;

            if (showLoading) {
                this.busyService.start();
            }

            return this.$http.post<ICancellationDates>(uri, dates)
                .then((response) => response.data)
                .finally(() => {
                    if (showLoading) {
                        this.busyService.stop();
                    }
                });
        }

        /**
         * Get list of indicator for one client id
         * @param {Guid} clientId - client id
         * @param {boolean} showLoading - show loading spinner. Default to true.
         * @returns {IClientMembershipIndicator} - Client Membership Indicators
         * */
        getClientMembershipIndicator(clientId: Guid, showLoading: boolean = false): ng.IPromise<any> {
            return this.getClientsMembershipIndicator([clientId], showLoading)
                .then((data:IClientMembershipIndicator[]) => {
                    if (data) {
                        return data[0];
                    }
                });
        }

        /**
         * Get list of indicators for Array client ids
         * @param {array} clientIds - clients id
         * @param {boolean} showLoading - show loading spinner. Default to true.
         * @returns {array} List of Client Membership Indicators
         * */
        getClientsMembershipIndicator(clientIds: Array<Guid>, showLoading: boolean = false): ng.IPromise<any> {
            let uri = `/${this.appSettings.API_ROOT}/Membership/Client/StatusIndicator/List`;

            if (showLoading) {
                this.busyService.start();
            }

            return this.$http.post(uri, { clientIds: clientIds })
                .then((response) => <IClientMembershipIndicator[]>response.data)
                .finally(() => {
                    if (showLoading) {
                        this.busyService.stop();
                    }
                });
        }

        /**
         * Get icon for Client Membership Indicator Enum
         * @param {enum} ClientMembershipIndicatorEnum - Client Membership Indicator
         * @returns {string} path to icon for enum item
         * */
        getClientMembershipIndicatorEnumIcon(num: ClientMembershipIndicatorEnum): string {
            return this._clientMembershipIndicatorEnumIcons[num];
        }

        /**
         * Get translate for Client Membership Indicator Status Enum
         * @param {enum} ClientMembershipIndicatorStatusEnum - Client Membership Indicator Status
         * @returns translated status for enum item
         * */
        getClientMembershipIndicatorStatusEnumText(num: ClientMembershipIndicatorStatusEnum): string {
            return this._clientMembershipIndicatorStatusEnum[num];
        }

        /**
         * Get list Services
         * */
        getClientMembershipServices(clientId: Guid, showLoading: boolean = false): ng.IPromise<any> {
            let uri = `/${this.appSettings.API_ROOT}/Membership/Client/${clientId}/Service/List`;

            if (showLoading) {
                this.busyService.start();
            }

            return this.$http.get(uri)
                .then((response) => response.data)
                .finally(() => {
                    if (showLoading) {
                        this.busyService.stop();
                    }
                });
        }

        static factory(): Function {
            let svc = (appSettings: Meevo.Components.Common.IAppSettings,
                       $http: ng.IHttpService,
                       $q: ng.IQService,
                       identityService: Meevo.Components.Common.IIdentityService,
                       $cookies: ng.cookies.ICookiesService,
                       busyService: Meevo.Components.Busy.BusyService) => {
                return new ClientMembershipDataService(appSettings, $http, $q, identityService, $cookies, busyService);
            };

            svc.$inject = [
                "meevo.AppSettings",
                "$http",
                "$q",
                "meevo.components.common.IdentityService",
                "$cookies",
                "meevo.components.busy.BusyService"
            ];
            return svc;
        }
    }

    angular.module("meevo.components.common")
        .factory("meevo.components.common.ClientMembershipDataService", ClientMembershipDataService.factory());
}
